import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import ClickOutside from './ClickOutside';
import Text from './Text';
import Icon from './Icon';
import Popup from './Popup';
import Container from './Container';
import Anchor from './Anchor';
import Separator from './Separator';

const colors = [
  {
    name: 'White',
    color: '#ffffff',
    borderColor: '#c4c4c4',
  },
  {
    name: 'Grey',
    color: '#c4c4c4',
  },
  {
    name: 'Dark Grey',
    color: '#757575',
  },
  {
    name: 'Black',
    color: '#000000',
  },
  {
    name: 'Red',
    color: '#c00001',
    inverse: '#ffffff',
  },
  {
    name: 'Yellow',
    color: '#ffbf04',
  },
  {
    name: 'Green',
    color: '#92d050',
  },
  {
    name: 'Dark Green',
    color: '#02b050',
  },
  {
    name: 'Light Blue',
    color: '#00b0f0',
  },
  {
    name: 'Blue',
    color: '#0270c0',
  },
  {
    name: 'Dark Blue',
    color: '#01205c',
  },
  {
    name: 'Purple',
    color: '#7030a0',
  },
];

const StyledLink = styled.a`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  text-decoration: none;
  width: ${props => props.width || '100%'};
  height: 40px;
  min-width: 150px;
  background-color: ${props => props.theme.background.background100};
  border-style: solid;
  border-width: 1px;
  border-radius: 0.3em;
  border-color: ${props => props.theme.border.border600};
  padding-left: ${props => props.theme.padding.xsmall};
  padding-right: ${props => props.theme.padding.normal};
  &.open {
    border-color: ${props => props.theme.border.border700};
  }
`;

const StyledPopup = styled(Popup)`
  left: ${props => (props.position === 'right' ? 'auto' : '0px')};
  right: ${props => (props.position === 'right' ? '0px' : 'auto')};
`;

const ColorPicker = ({
  data,
  text,
  selected: defaultSelected,
  onSelected,
  position,
  ...props
}) => {
  const [show, setShow] = useState(false);
  const [selected, setSelected] = useState(
    colors.find(item => item.color === defaultSelected)
  );

  const onClick = useCallback(item => {
    setSelected(item);
    setShow(false);
  }, []);

  useEffect(() => onSelected(selected), [onSelected, selected]);

  return (
    <ClickOutside onClickOutside={() => setShow(false)} {...props}>
      <StyledLink
        className={show ? 'open' : ''}
        onClick={() => setShow(!show)}
        {...props}
      >
        <Container
          height="32px"
          width="32px"
          borderRadius="4px"
          backgroundColor={selected ? selected.color : '#ffffff'}
          backgroundSize={selected ? 'auto' : '20px 20px'}
          backgroundPosition={selected ? '0% 0%' : '0 0, 10px 10px'}
          backgroundImage={
            selected
              ? 'none'
              : `linear-gradient(45deg, #ddd 25%, transparent 25%, transparent 75%, #ddd 75%, #ddd), 
                        linear-gradient(45deg, #ddd 25%, transparent 25%, transparent 75%, #ddd 75%, #ddd)`
          }
          border={`solid 1px ${
            selected ? selected.borderColor || selected.color : '#ddd'
          }`}
        />
        <Text
          paddingHorizontal
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {text}
        </Text>
        <Icon
          className="ls-icon icon-generalcaretdownmedium"
          iconSize="medium"
        />
      </StyledLink>
      <StyledPopup
        show={show}
        position={position}
        minWidth="208px"
        width="208px"
      >
        <Anchor onClick={() => onClick(null)}>
          <Container padding display="flex" alignItems="center">
            <Icon
              className="zmdi zmdi-format-color-reset"
              iconSize="normal"
              iconColor="normal"
            />
            <Text color="normal" paddingHorizontal="small">
              Transparent
            </Text>
          </Container>
        </Anchor>
        <Separator />
        <Container display="flex" flexWrap="wrap" padding="small">
          {data.map(item => (
            <Container key={item.name} padding="small">
              <Anchor title={item.name} onClick={() => onClick(item)}>
                <Container
                  backgroundColor={item.color}
                  border={`solid 1px ${item.borderColor || item.color}`}
                  height="32px"
                  width="32px"
                  borderRadius="4px"
                />
              </Anchor>
            </Container>
          ))}
        </Container>
      </StyledPopup>
    </ClickOutside>
  );
};

ColorPicker.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape()),
  text: PropTypes.string,
  selected: PropTypes.string,
  onSelected: PropTypes.func,
  position: PropTypes.string,
};

ColorPicker.defaultProps = {
  data: colors,
  text: 'Colour',
  selected: null,
  onSelected: () => {},
  position: null,
};

export default ColorPicker;
